import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { DEV_LONG_PRESS_MS, devToolsUnlocked } from '../../../lib/dev-tools';
import { requestDevToolsAccess } from '../../../lib/dev-tools-access';
import { settingsKeys, type Recipient } from '../../settings/shared/settings-queries';

const MONITORING_BASE_URL = 'https://bo.wix.com/pages/symphony-live/conversation-monitoring';
const NOTICE_MS = 2000;

export type MonitorNotice = { url: string; copied: boolean };

/**
 * Long-press the chat's expand/shrink control to copy THIS conversation's Symphony Live BO monitoring
 * URL. Most of the time the dev only needs to send the link to someone, so the press copies it and
 * raises a short-lived `notice`; opening the page is one click on that notice. The dev counterpart of the brand mark's long-press — same hold
 * (`DEV_LONG_PRESS_MS`), same gate, and the same rule that the hold never doubles as the control's
 * ordinary click.
 *
 * Three things it is careful about, all of them properties the brand-mark gesture established:
 *
 *  - **The verdict is asked for on the press FIRING, never on mount or on an ordinary click**, so a
 *    real user's chat costs no request at all. A dev build (or a tab that already earned the grant)
 *    short-circuits `devToolsUnlocked()` synchronously, which is also what keeps the common case
 *    inside the browser's user-activation window — `window.open` off a timer that then awaited a
 *    round trip is a popup a blocker may eat.
 *  - **The release is swallowed either way.** A half-second hold is not a click, so a press with no
 *    grant does nothing rather than expanding the chat — exactly what the brand mark does with the
 *    sidebar. That is why `consumeLongPress` exists and why the caller must consult it.
 *  - **No conversation, no gesture.** There is nothing to monitor on a brand-new chat, so the timer
 *    is never even armed.
 *
 * The metasite comes from the recipient the app has ALREADY read (the sidebar's phone gate keeps
 * `settingsKeys.recipient` warm on every page), so this normally resolves out of the cache with no
 * request; the on-demand read is the cold-cache fallback and only ever runs for an unlocked caller.
 */
export function useConversationMonitorLongPress(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  /** Whether the CURRENT press already fired as a long press — the click after it must be swallowed. */
  const firedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [notice, setNotice] = useState<MonitorNotice | null>(null);
  const conversationIdRef = useRef(conversationId);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    },
    [],
  );

  // The hook stays mounted across chat switches: drop the notice, and let an in-flight press see it is stale.
  useEffect(() => {
    conversationIdRef.current = conversationId;
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice(null);
  }, [conversationId]);

  const showNotice = useCallback((next: MonitorNotice) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice(next);
    noticeTimerRef.current = setTimeout(() => setNotice(null), NOTICE_MS);
  }, []);

  const resolveMetaSiteId = useCallback(async (): Promise<string | undefined> => {
    const cached = queryClient.getQueryData<Recipient | null>(settingsKeys.recipient);
    if (cached?.metaSiteId) return cached.metaSiteId;
    try {
      const res = await apiFetch(`${API_BASE}/api/recipient`, { credentials: 'include' });
      if (!res.ok) return undefined;
      return ((await res.json()) as { recipient?: Recipient }).recipient?.metaSiteId;
    } catch {
      return undefined;
    }
  }, [queryClient]);

  const start = useCallback(() => {
    firedRef.current = false;
    if (!conversationId) return;
    const pressedOn = conversationId;
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      void (async () => {
        if (!devToolsUnlocked() && !(await requestDevToolsAccess())) return;
        const metaSiteId = await resolveMetaSiteId();
        if (!metaSiteId) return;
        const url = `${MONITORING_BASE_URL}?metaSiteId=${encodeURIComponent(metaSiteId)}&conversationId=${encodeURIComponent(conversationId)}`;
        let copied = true;
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          copied = false;
        }
        if (conversationIdRef.current !== pressedOn) return;
        showNotice({ url, copied });
      })();
    }, DEV_LONG_PRESS_MS);
  }, [conversationId, resolveMetaSiteId, showNotice]);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  /** True when the release belongs to a long press, and clears the flag. The caller returns early. */
  const consumeLongPress = useCallback((): boolean => {
    const fired = firedRef.current;
    firedRef.current = false;
    return fired;
  }, []);

  const openMonitor = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setNotice(null);
  }, []);

  return {
    /** Set for 2 seconds after a press copies the URL; `copied` is false when the browser refused the write. */
    notice,
    openMonitor,
    /** Spread onto the control. Pointer events, so a touch hold works the same as a mouse hold. */
    handlers: {
      onPointerDown: start,
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onPointerCancel: cancel,
    },
    consumeLongPress,
  };
}
